import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, ArrowLeftIcon, MailIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
interface Section {
  title: string;
  content: React.ReactNode;
}
const sections: Section[] = [{
  title: '1. Information We Collect',
  content: <div className="space-y-5" data-id="element-2564">
        <p data-id="element-2565">We may collect the following types of information:</p>
        <div data-id="element-2566">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-2567">Personal Information</h4>
          <p className="mb-2" data-id="element-2568">You may voluntarily provide:</p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2569">
            <li data-id="element-2570">Name</li>
            <li data-id="element-2571">Email address</li>
            <li data-id="element-2572">Phone number</li>
            <li data-id="element-2573">Billing/payment information</li>
            <li data-id="element-2574">Mailing address</li>
            <li data-id="element-2575">Account login information</li>
            <li data-id="element-2576">Questionnaire or onboarding responses</li>
            <li data-id="element-2577">Appointment or scheduling details</li>
            <li data-id="element-2578">Travel preferences</li>
            <li data-id="element-2579">Coaching application information</li>
            <li data-id="element-2580">User-generated content, messages, reviews, or feedback</li>
          </ul>
        </div>
        <div data-id="element-2581">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-2582">
            Automatically Collected Information
          </h4>
          <p className="mb-2" data-id="element-2583">
            When you use our website or apps, we may automatically collect:
          </p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2584">
            <li data-id="element-2585">Device type</li>
            <li data-id="element-2586">Browser type</li>
            <li data-id="element-2587">IP address</li>
            <li data-id="element-2588">App usage data</li>
            <li data-id="element-2589">Pages visited</li>
            <li data-id="element-2590">Click activity</li>
            <li data-id="element-2591">Referral sources</li>
            <li data-id="element-2592">Cookies and analytics data</li>
            <li data-id="element-2593">Approximate location information</li>
          </ul>
        </div>
        <div data-id="element-2594">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-2595">
            Sensitive or Wellness-Related Information
          </h4>
          <p className="mb-2" data-id="element-2596">
            Some services may involve discussions related to:
          </p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2597">
            <li data-id="element-2598">Mental wellness</li>
            <li data-id="element-2599">Lifestyle habits</li>
            <li data-id="element-2600">Stress management</li>
            <li data-id="element-2601">Organization and productivity</li>
            <li data-id="element-2602">Personal goals</li>
          </ul>
          <p className="mt-2" data-id="element-2603">
            We do not provide medical or mental health treatment services. Any
            information shared is voluntary.
          </p>
        </div>
      </div>
}, {
  title: '2. How We Use Your Information',
  content: <div data-id="element-2604">
        <p className="mb-3" data-id="element-2605">We may use your information to:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2606">
          <li data-id="element-2607">Provide and improve our services</li>
          <li data-id="element-2608">Process purchases and subscriptions</li>
          <li data-id="element-2609">Deliver digital products, courses, or checklists</li>
          <li data-id="element-2610">Schedule coaching sessions or consultations</li>
          <li data-id="element-2611">Respond to inquiries and support requests</li>
          <li data-id="element-2612">Personalize app experiences</li>
          <li data-id="element-2613">Generate customized schedules, reminders, or recommendations</li>
          <li data-id="element-2614">Provide website and app development services</li>
          <li data-id="element-2615">Deliver branding or business support services</li>
          <li data-id="element-2616">Send newsletters, updates, promotions, or announcements</li>
          <li data-id="element-2617">Improve website performance and user experience</li>
          <li data-id="element-2618">Monitor app functionality and prevent abuse</li>
          <li data-id="element-2619">Comply with legal obligations</li>
        </ul>
      </div>
}, {
  title: '3. Coaching & Educational Disclaimer',
  content: <div className="space-y-3" data-id="element-2620">
        <p data-id="element-2621">Trippin' with Mandy may offer:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2622">
          <li data-id="element-2623">Coaching</li>
          <li data-id="element-2624">Motivational content</li>
          <li data-id="element-2625">Wellness discussions</li>
          <li data-id="element-2626">Organization systems</li>
          <li data-id="element-2627">Lifestyle guidance</li>
          <li data-id="element-2628">Travel tips</li>
          <li data-id="element-2629">Educational resources</li>
          <li data-id="element-2630">Business guidance</li>
        </ul>
        <p data-id="element-2631">
          These services are for informational and educational purposes only and
          are not medical, legal, financial, or psychological advice.
        </p>
        <p data-id="element-2632">
          If you are experiencing a medical or mental health emergency, please
          contact a licensed professional or emergency services.
        </p>
      </div>
}, {
  title: '4. Website, App Development & Business Services',
  content: <div className="space-y-3" data-id="element-2633">
        <p data-id="element-2634">Trippin' with Mandy may provide:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2635">
          <li data-id="element-2636">Website design and development</li>
          <li data-id="element-2637">App development assistance</li>
          <li data-id="element-2638">Branding and content creation</li>
          <li data-id="element-2639">AI-assisted business tools</li>
          <li data-id="element-2640">Digital setup assistance</li>
          <li data-id="element-2641">Business consulting and organizational support</li>
        </ul>
        <p data-id="element-2642">
          Clients may voluntarily provide business-related information, branding
          materials, login credentials, project requirements, content, or other
          materials necessary to complete requested services.
        </p>
        <p data-id="element-2643">
          We take reasonable precautions to protect shared information; however,
          clients are responsible for maintaining the security of their own
          accounts, passwords, hosting platforms, third-party services, and
          connected tools.
        </p>
        <p data-id="element-2644">Trippin' with Mandy is not responsible for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2645">
          <li data-id="element-2646">Third-party platform outages</li>
          <li data-id="element-2647">Hosting failures</li>
          <li data-id="element-2648">App store approval decisions</li>
          <li data-id="element-2649">Third-party software limitations</li>
          <li data-id="element-2650">Data loss caused by external platforms or user actions</li>
          <li data-id="element-2651">Ongoing maintenance unless otherwise agreed upon</li>
        </ul>
        <p data-id="element-2652">
          Clients retain ownership of their original content and branding
          materials unless otherwise specified in writing.
        </p>
      </div>
}, {
  title: '5. Travel Services & Recommendations',
  content: <div className="space-y-3" data-id="element-2653">
        <p data-id="element-2654">
          Travel-related information, recommendations, packing guides,
          checklists, and destination suggestions are provided for general
          informational purposes only.
        </p>
        <p data-id="element-2655">Users are responsible for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2656">
          <li data-id="element-2657">Verifying travel requirements</li>
          <li data-id="element-2658">Reviewing airline/cruise policies</li>
          <li data-id="element-2659">Obtaining travel insurance if desired</li>
          <li data-id="element-2660">Confirming passport, visa, and vaccination requirements</li>
        </ul>
        <p data-id="element-2661">
          Trippin' with Mandy is not responsible for third-party travel
          disruptions, cancellations, delays, injuries, or losses.
        </p>
      </div>
}, {
  title: '6. Purchases & Payments',
  content: <div className="space-y-3" data-id="element-2662">
        <p data-id="element-2663">Payments may be processed through third-party providers such as:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2664">
          <li data-id="element-2665">Stripe</li>
          <li data-id="element-2666">PayPal</li>
          <li data-id="element-2667">Gumroad</li>
          <li data-id="element-2668">Google Play</li>
          <li data-id="element-2669">Apple App Store</li>
          <li data-id="element-2670">Other payment processors</li>
        </ul>
        <p data-id="element-2671">We do not store full payment card information on our servers.</p>
        <p data-id="element-2672">
          Please review the privacy policies of any third-party payment provider
          you use.
        </p>
      </div>
}, {
  title: '7. Cookies & Analytics',
  content: <div className="space-y-3" data-id="element-2673">
        <p data-id="element-2674">We may use:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2675">
          <li data-id="element-2676">Cookies</li>
          <li data-id="element-2677">Pixels</li>
          <li data-id="element-2678">Analytics tools</li>
          <li data-id="element-2679">Advertising technologies</li>
        </ul>
        <p data-id="element-2680">These tools help us:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2681">
          <li data-id="element-2682">Understand website traffic</li>
          <li data-id="element-2683">Improve performance</li>
          <li data-id="element-2684">Personalize experiences</li>
          <li data-id="element-2685">Measure marketing effectiveness</li>
        </ul>
        <p data-id="element-2686">You may disable cookies through your browser settings.</p>
      </div>
}, {
  title: '8. Email & Marketing Communications',
  content: <div className="space-y-3" data-id="element-2687">
        <p data-id="element-2688">
          If you subscribe to newsletters, download resources, create an
          account, make a purchase, or contact us through forms, we may send:
        </p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2689">
          <li data-id="element-2690">Service updates</li>
          <li data-id="element-2691">Promotional emails</li>
          <li data-id="element-2692">Product announcements</li>
          <li data-id="element-2693">App updates</li>
          <li data-id="element-2694">Event or livestream information</li>
          <li data-id="element-2695">Business or coaching communications</li>
        </ul>
        <p data-id="element-2696">
          You may unsubscribe at any time using the unsubscribe link in emails.
        </p>
      </div>
}, {
  title: '9. Third-Party Services',
  content: <div className="space-y-3" data-id="element-2697">
        <p data-id="element-2698">
          Our services may integrate with third-party platforms, including but
          not limited to:
        </p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2699">
          <li data-id="element-2700">Google services</li>
          <li data-id="element-2701">Firebase</li>
          <li data-id="element-2702">TikTok</li>
          <li data-id="element-2703">Calendars</li>
          <li data-id="element-2704">Payment processors</li>
          <li data-id="element-2705">Scheduling systems</li>
          <li data-id="element-2706">Social media platforms</li>
          <li data-id="element-2707">Analytics providers</li>
          <li data-id="element-2708">Hosting providers</li>
          <li data-id="element-2709">AI tools and automation platforms</li>
        </ul>
        <p data-id="element-2710">
          We are not responsible for the privacy practices of third-party
          services.
        </p>
      </div>
}, {
  title: '10. Data Security',
  content: <div className="space-y-3" data-id="element-2711">
        <p data-id="element-2712">We take reasonable measures to protect your information using:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2713">
          <li data-id="element-2714">Secure platforms</li>
          <li data-id="element-2715">Password protections</li>
          <li data-id="element-2716">Encrypted services where available</li>
          <li data-id="element-2717">Limited access controls</li>
        </ul>
        <p data-id="element-2718">
          However, no method of internet transmission or storage is 100% secure.
        </p>
      </div>
}, {
  title: "11. Children's Privacy",
  content: <div className="space-y-3" data-id="element-2719">
        <p data-id="element-2720">
          Our services are not directed toward children under 13 years old. We
          do not knowingly collect personal information from children.
        </p>
        <p data-id="element-2721">
          If we become aware that information from a child under 13 has been
          collected, we will delete it promptly.
        </p>
      </div>
}, {
  title: '12. Your Rights',
  content: <div className="space-y-3" data-id="element-2722">
        <p data-id="element-2723">Depending on your location, you may have rights to:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2724">
          <li data-id="element-2725">Access your data</li>
          <li data-id="element-2726">Correct inaccurate information</li>
          <li data-id="element-2727">Request deletion of your data</li>
          <li data-id="element-2728">Opt out of marketing communications</li>
          <li data-id="element-2729">Request information about data usage</li>
        </ul>
        <p data-id="element-2730">To make a request, contact us using the information below.</p>
      </div>
}, {
  title: '13. User Content & Community Features',
  content: <div className="space-y-3" data-id="element-2731">
        <p data-id="element-2732">
          If you post content in public areas, communities, chats, livestreams,
          games, or interactive features, that information may be visible to
          other users.
        </p>
        <p data-id="element-2733">Please avoid sharing sensitive personal information publicly.</p>
      </div>
}, {
  title: '14. App Features & Notifications',
  content: <div className="space-y-3" data-id="element-2734">
        <p data-id="element-2735">Some apps or services may request permission for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2736">
          <li data-id="element-2737">Push notifications</li>
          <li data-id="element-2738">Calendar syncing</li>
          <li data-id="element-2739">Location access</li>
          <li data-id="element-2740">Camera or photo uploads</li>
          <li data-id="element-2741">Contacts or reminders</li>
        </ul>
        <p data-id="element-2742">Permissions can typically be managed in your device settings.</p>
      </div>
}, {
  title: '15. Changes to This Policy',
  content: <div className="space-y-3" data-id="element-2743">
        <p data-id="element-2744">
          We may update this Privacy Policy periodically. Updated versions will
          be posted with a revised effective date.
        </p>
        <p data-id="element-2745">
          Continued use of our services after changes means you accept the
          updated policy.
        </p>
      </div>
}, {
  title: '16. Contact Information',
  content: <div className="space-y-3" data-id="element-2746">
        <p data-id="element-2747">For questions, concerns, or privacy requests, contact:</p>
        <a href="mailto:trippinwithmandy@gmail.com" className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-blush-100 hover:bg-blush-200 text-plum-800 font-bold transition-colors" data-id="element-2748">
          <MailIcon className="w-5 h-5" data-id="element-2749" />
          trippinwithmandy@gmail.com
        </a>
      </div>
}];
export function PrivacyPolicyPage() {
  return <div className="min-h-screen bg-gradient-to-b from-blush-50 via-white to-lavender-50 pt-28 pb-24" data-id="element-2750">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2751">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-plum-500 hover:text-plum-700 font-bold text-sm mb-8 transition-colors" data-id="element-2752">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-2753" />
          Back to Home
        </Link>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12" data-id="element-2754">
          <div className="flex justify-center mb-4" data-id="element-2755">
            <GlitterHeart size={32} data-id="element-2756" />
          </div>
          <div className="w-16 h-16 rounded-2xl bg-blush-100 flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-2757">
            <ShieldCheckIcon className="w-8 h-8 text-cta-500" data-id="element-2758" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-plum-800 mb-3" data-id="element-2759">
            Privacy Policy
          </h1>
          <p className="text-plum-500 font-medium" data-id="element-2760">For Trippin' with Mandy</p>
          <p className="text-plum-400 text-sm font-medium mt-1" data-id="element-2761">
            Effective Date: May 12, 2026
          </p>
        </motion.div>

        {/* Intro */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-3xl p-8 shadow-sm border border-blush-100 mb-8" data-id="element-2762">
          <p className="text-plum-700 leading-relaxed mb-4" data-id="element-2763">
            Welcome to Trippin' with Mandy. Your privacy matters to us. This
            Privacy Policy explains how we collect, use, store, and protect your
            information across our websites, apps, digital products, coaching
            services, travel-related services, business services, games,
            communities, and related platforms operated under the Trippin' with
            Mandy brand, including but not limited to Trippin' Through Life
            (TTL) and affiliated projects.
          </p>
          <p className="text-plum-700 leading-relaxed font-medium" data-id="element-2764">
            By using our services, you agree to the practices described in this
            Privacy Policy.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6" data-id="element-2765">
          {sections.map((section, idx) => <motion.section key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.03
        }} className="bg-white rounded-3xl p-8 shadow-sm border border-blush-100" data-id="element-2766">
              <h2 className="font-heading text-2xl font-bold text-plum-800 mb-4" data-id="element-2767">
                {section.title}
              </h2>
              <div className="text-plum-600 leading-relaxed space-y-3" data-id="element-2768">
                {section.content}
              </div>
            </motion.section>)}
        </div>

        {/* Closing */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center mt-12 text-plum-500 text-sm font-medium italic px-4" data-id="element-2769">
          By using Trippin' with Mandy services, websites, apps, products,
          games, or digital platforms, you acknowledge that you have read and
          agreed to this Privacy Policy.
        </motion.div>

        {/* Footer links */}
        <div className="text-center mt-10 flex flex-wrap justify-center gap-3" data-id="element-2770">
          <Link to="/terms" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-plum-200 text-plum-700 hover:bg-plum-50 font-bold text-sm transition-colors" data-id="element-2771">
            View Terms & Conditions
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cta-500 text-white hover:bg-cta-600 font-bold text-sm transition-colors shadow-lg shadow-cta-500/20" data-id="element-2772">
            Back to Home
          </Link>
        </div>
      </div>
    </div>;
}